import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { combineLatest, map, share, switchMap } from 'rxjs/operators';
import { DutyLogService } from 'src/app/core/duty-log.service';
import { USER } from 'src/app/core/user.service';
import { FindFlightDialogComponent } from 'src/app/flight-logs/components/find-flight-dialog/find-flight-dialog.component';
import { FlightLogSummaryTableSort as FlightLogSummarySort } from 'src/app/shared/components/flight-log-summaries-table/flight-log-summaries-table.component';
import {
  LogMode,
  tryGetSort,
} from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { FlightLogHttpClient } from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.service';
import { sleep } from 'src/util/async';
import { distinctUntilObjectChanged } from 'src/util/rxjs/operators';
import { getSkipTakePageEvent } from 'src/util/ui/pagination';
import { UserViewModel } from 'src/view-models/user-view-model';
import { FlightLogHighlightsViewModel } from '../../../../../view-models/flight-log-highlights-view-model';
import {
  FlightLogHighlightsTableColumn,
  FlightLogHighlightsTableComponent,
} from '../../../../shared/components/flight-log-highlights-table/flight-log-highlights-table.component';

@Component({
  selector: 'fl-pilot-flight-logs',
  templateUrl: './pilot-flight-logs.component.html',
  styleUrls: ['./pilot-flight-logs.component.scss'],
})
export class PilotFlightLogsComponent implements OnInit {
  public columns: FlightLogHighlightsTableColumn[] = [
    'flightTime',
    'flightType',
    'flightSummaryId',
    'tailNumber',
    'status',
    'flightStatus',
    'actions',
  ];

  public defaultPageSize = 5;
  public defaultSort: FlightLogSummarySort = {
    orderBy: 'flightTime',
    direction: 'desc',
  };
  public sort$ = new BehaviorSubject<FlightLogSummarySort>(this.defaultSort);
  public page$ = new BehaviorSubject<{ skip: number; take: number }>({
    skip: 0,
    take: this.defaultPageSize,
  });
  public total$: Observable<number>;

  public flightLogs$: Observable<FlightLogHighlightsViewModel[]>;
  public _pageRefresh$ = new BehaviorSubject({});

  @ViewChild(FlightLogHighlightsTableComponent, { static: true })
  public table: FlightLogHighlightsTableComponent;

  constructor(
    @Inject(USER) public readonly user: UserViewModel,
    private _snackBar: MatSnackBar,
    private _router: Router,
    private _flightLogs: FlightLogHttpClient,
    private _dutyLogs: DutyLogService,
    private _dialog: MatDialog,
  ) {}

  public ngOnInit() {
    const flightLogResults$ = this._dutyLogs.refreshUserDutyLogs().pipe(
      switchMap(() => this.user.latestShift$),
      distinctUntilObjectChanged(),
      // tslint:disable-next-line: deprecation
      combineLatest(this.sort$, this.page$, this._pageRefresh$),
      switchMap(([shift, sort, { skip, take }]) => {
        this.table.markAsLoading();
        return this._flightLogs.getManyWithHighlights({
          employeeId: this.user.id,
          openOnly: true,
          currentBaseId: shift != null ? shift.baseId : undefined,
          currentAircraftId: shift != null ? shift.aircraftId : undefined,
          skip,
          limit: take,
          sort: tryGetSort(sort),
        });
      }),
      share(),
    );
    this.flightLogs$ = flightLogResults$.pipe(
      map((r) => {
        this.table.markAsLoaded();
        return r.results;
      }),
    );
    this.total$ = flightLogResults$.pipe(map((r) => r.totalCount));
  }

  public async begin(flightLogId: string, mode: LogMode) {
    try {
      await this._flightLogs.begin(flightLogId, mode).toPromise();
      await this._router.navigate([`/flight-logs/${flightLogId}/edit`]);
      this._snackBar.open(`Successfully started log for flight ${flightLogId}.`, undefined, {
        duration: 3000,
      });
    } catch (err) {
      const error = `There was an error starting the log for flight ${flightLogId}. `;
      this._snackBar.open(error, undefined, { duration: 3000 });
    }
  }

  public onPage(page: PageEvent) {
    this.page$.next(getSkipTakePageEvent(page));
  }

  public onSort(sort: FlightLogSummarySort) {
    this.sort$.next(sort);
  }

  public async refresh(delay?: number) {
    this.table.markAsLoading();
    if (delay != null) {
      await sleep(delay);
    }
    this._pageRefresh$.next({});
  }

  public openFindFlightDialog() {
    const config: MatDialogConfig = {
      disableClose: true,
    };
    this._dialog.open(FindFlightDialogComponent, config);
  }
}
